import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface FieldAlbum {
  id: string;
  title: string;
  slug?: string;
  category: string;
  content_type?: string;
  video_url?: string;
  video_id?: string;
  video_thumbnail_custom?: string;
  event_date?: string;
  location?: string;
  short_description?: string;
  description?: string;
  cover_image?: string;
  external_link?: string;
  display_order?: number;
  featured_home?: boolean;
  photo_count?: number;
}

export interface FieldPhoto {
  id: string;
  album_id: string;
  image_url: string;
  thumbnail_url?: string;
  alt_text?: string;
  caption?: string;
  display_order?: number;
}

export const FIELD_CATEGORIES: { value: string; label: string }[] = [
  { value: 'evenements', label: 'Événements' },
  { value: 'partenariats', label: 'Partenariats' },
  { value: 'formations', label: 'Formations' },
  { value: 'demonstrations', label: 'Démonstrations' },
  { value: 'videos', label: 'Vidéos' },
  { value: 'terrain', label: 'Terrain' },
  { value: 'medias', label: 'Médias' },
  { value: 'stations', label: 'Stations de recharge' },
];

/**
 * Charge les albums actifs. featuredOnly=true → uniquement ceux mis en avant (home, max 6).
 */
export function useFieldAlbums(featuredOnly = false) {
  const [albums, setAlbums] = useState<FieldAlbum[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!supabase) { setLoading(false); return; }
      try {
        let q = supabase
          .from('field_albums')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false });
        if (featuredOnly) q = q.eq('featured_home', true);

        const { data, error } = await q;
        if (!mounted) return;
        if (error) { console.error('[useFieldAlbums]', error.message); setLoading(false); return; }

        let rows = (data || []) as FieldAlbum[];
        if (featuredOnly) rows = rows.slice(0, 6);

        // Comptage des photos actives par album (pour le badge)
        const ids = rows.map((r) => r.id);
        if (ids.length) {
          const { data: photos } = await supabase
            .from('field_photos')
            .select('album_id')
            .eq('is_active', true)
            .in('album_id', ids);
          const counts: Record<string, number> = {};
          (photos || []).forEach((p: any) => { counts[p.album_id] = (counts[p.album_id] || 0) + 1; });
          rows = rows.map((r) => ({ ...r, photo_count: counts[r.id] || 0 }));
        }
        setAlbums(rows);
      } catch (e) {
        console.error('[useFieldAlbums] exception', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();

    let channel: ReturnType<NonNullable<typeof supabase>['channel']> | undefined;
    try {
      channel = supabase?.channel('field-albums-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'field_albums' }, () => load())
        .subscribe();
    } catch (err) {
      console.warn('[useFieldAlbums] realtime indisponible', err);
    }
    return () => { mounted = false; try { channel?.unsubscribe(); } catch { /* noop */ } };
  }, [featuredOnly]);

  return { albums, loading };
}

/** Charge les photos actives d'un album. */
export function useAlbumPhotos(albumId?: string) {
  const [photos, setPhotos] = useState<FieldPhoto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!supabase || !albumId) { setLoading(false); return; }
      setLoading(true);
      const { data, error } = await supabase
        .from('field_photos')
        .select('*')
        .eq('album_id', albumId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (!mounted) return;
      if (error) console.error('[useAlbumPhotos]', error.message);
      setPhotos((data || []) as FieldPhoto[]);
      setLoading(false);
    };
    load();
    return () => { mounted = false; };
  }, [albumId]);

  return { photos, loading };
}
